import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import { CourseRepository } from "../repository/courseRepository";
import { Course, CourseStatus, GetCoursesRequstDto } from "../models/course";
import { CreateCourseRequestDto } from "../models/createCourseRequestDto";
import { UpdateCourseRequestDto } from "../models/updateCourseRequestDto";
import {
  CourseChapter,
  GetCourseChapterRequestDto,
} from "../models/courseChapter";
import { CreateChapterRequestDto } from "../models/createChapter";
import { UpdateChapterRequestDto } from "../models/updateChapterRequestDto";
import { GetLessonRequestDto, Lesson } from "../models/lesson";
import {
  ModalState,
  useModal,
} from "@/core/components/utils/modals/store/modalStore";
import { formateResponse } from "@/core/utils/functions";
import { CreateLessonRequestDto } from "../models/createLessonRequestDto";
import { UpdateLessonRequestDto } from "../models/updateLessonRequstDto";
import { CreateCourseContentRequestDto } from "../models/content";
import { AxiosProgressEvent } from "axios";

interface CourseState {
  courseFilter: GetCoursesRequstDto;
  chapterFilter: GetCourseChapterRequestDto;
  lessonFilter: GetLessonRequestDto;
  coursesContent: {
    data: Course[];
    pagination: Pagination;
  };
  chapters: {
    data: CourseChapter[];
    pagination: Pagination;
  };
  lessons: {
    data: Lesson[];
    pagination: Pagination;
  };
}

export const useCourse = defineStore("course", () => {
  const toast = useToast();
  const loading = ref<boolean>(false);
  const uploadAtachmentProgress = ref<number>(0);
  const _repo = CourseRepository.getInstance();

  const {
    showAlertModal,
    showSuccessModal,
    showDeleteModal,
    showConfirmationModel,
    showExportFileModal,
  } = useModal();

  const state = reactive<CourseState>({
    courseFilter: {
      pageNumber: 1,
      pageSize: 50,
      title: "",
      gradeId: "",
      subjectId: "",
      status: CourseStatus.ACTIVE,
    },
    coursesContent: {
      data: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        last_page: 1,
        count: 0,
      },
    },
    chapterFilter: {
      pageNumber: 1,
      pageSize: 10,
      courseId: "",
    },
    chapters: {
      data: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        last_page: 1,
        count: 0,
      },
    },
    lessonFilter: {
      chapterId: "",
      pageNumber: 1,
      pageSize: 10,
    },
    lessons: {
      data: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        last_page: 1,
        count: 0,
      },
    },
  });

  const fetchCourse = async (id: string): Promise<Course> => {
    try {
      loading.value = true;
      const response = await _repo.getCourseById(id);
      return response.data.content;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const fetchCourses = async (): Promise<void> => {
    try {
      loading.value = true;
      console.log(state.courseFilter);
      const response = await _repo.getCourses(state.courseFilter);

      const { data, message, pagination, traceId } = formateResponse(
        response.data
      );

      state.coursesContent.data = data;
      state.coursesContent.pagination = pagination;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const createCourse = async (
    course: CreateCourseRequestDto
  ): Promise<void> => {
    try {
      loading.value = true;
      const formData = new FormData();
      formData.append("title", course.title);
      formData.append("description", course.description);
      formData.append("subjectId", course.subjectId);
      formData.append("teacherId", course.teacherId);
      formData.append("photo", course.photo);
      formData.append("gradeId", course.gradeId);
      formData.append("language", course.language.toString() as string);
      await _repo.createCourse(formData);
      toast.success("Course created successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const updateCourse = async (
    id: string,
    course: UpdateCourseRequestDto
  ): Promise<void> => {
    try {
      loading.value = true;
      await _repo.updateCourse(id, course);
      toast.success("Course updated successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const deleteCourse = async (id: string): Promise<void> => {
    const { modalState, adminPassword } = await showConfirmationModel({
      title: "Delete Course",
      message: "Are you sure you want to delete this course?",
    });

    if (modalState === ModalState.Canceled) return;
    try {
      loading.value = true;
      await _repo.deleteCourse(id);
      toast.success("Course deleted successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const toggleCourseActivation = async (id: string): Promise<void> => {
    try {
      loading.value = true;
      await _repo.activateCourse(id);
      toast.success("Course status changed successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const fetchChapter = async (courseId: string, chapterId: string) => {
    try {
      loading.value = true;
      const { data } = await _repo.getChapter(courseId, chapterId);
      return data.content;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };
  const fetchChapters = async () => {
    try {
      loading.value = true;
      const response = await _repo.getChapters(state.chapterFilter);
      const { data, message, pagination, traceId } = formateResponse(
        response.data
      );
      state.chapters.data = data;
      state.chapters.pagination = pagination;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const createChapter = async (
    courseId: string,
    request: CreateChapterRequestDto
  ) => {
    try {
      loading.value = true;
      await _repo.createChapter(courseId, request);
      toast.success("Chapter created successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const updateChapter = async (
    courseId: string,
    chapterId: string,
    request: UpdateChapterRequestDto
  ) => {
    try {
      loading.value = true;
      await _repo.updateChapter(courseId, chapterId, request);
      toast.success("Chapter created successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const deleteChapter = async (courseId: string, chapterId: string) => {
    const { modalState, adminPassword } = await showConfirmationModel({
      title: "Delete Chapter",
      message: "Are you sure you want to delete this chapter?",
    });

    if (modalState === ModalState.Canceled) return;
    try {
      loading.value = true;
      await _repo.deleteChapter(courseId, chapterId);
      toast.success("Chapter deleted successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const fetchLessons = async () => {
    try {
      loading.value = true;
      const response = await _repo.getLessons(state.lessonFilter);
      const { data, message, pagination, traceId } = formateResponse(
        response.data
      );
      state.lessons.data = data;
      state.lessons.pagination = pagination;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const fetchLesson = async (
    courseId: string,
    chapterId: string,
    lessonId: string
  ) => {
    try {
      loading.value = true;
      const { data } = await _repo.getLesson(courseId, chapterId, lessonId);
      return data.content;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const updateLesson = async (
    courseId: string,
    chapterId: string,
    lessonId: string,
    lesson: UpdateLessonRequestDto
  ) => {
    try {
      loading.value = true;
      const formData = new FormData();
      formData.append("title", lesson.title);
      formData.append("description", lesson.description);
      formData.append("isPreview", lesson.isPreview.toString());
      formData.append("chapterId", chapterId);
      formData.append("courseId", courseId);
      formData.append("lessonId", lessonId);
      formData.append("order", lesson.order.toString());
      if (lesson.thumbnail) formData.append("thumbnail", lesson.thumbnail);

      const { data } = await _repo.updateLesson(
        courseId,
        chapterId,
        lessonId,
        formData
      );
      toast.success(data?.message);
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const createLesson = async (
    courseId: string,
    chapterId: string,
    lesson: CreateLessonRequestDto
  ) => {
    try {
      loading.value = true;
      const formData = new FormData();
      formData.append("title", lesson.title);
      formData.append("description", lesson.description);
      formData.append("isPreview", lesson.isPreview.toString());
      formData.append("chapterId", chapterId);
      formData.append("courseId", courseId);
      formData.append("order", lesson.order.toString());
      if (lesson.thumbnail) formData.append("thumbnail", lesson.thumbnail);
      const { data } = await _repo.createLesson(courseId, chapterId, formData);
      toast.success(data?.message);
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const deleteLesson = async (
    courseId: string,
    chapterId: string,
    lessonId: string
  ) => {
    const { modalState, adminPassword } = await showConfirmationModel({
      title: "Delete Lesson",
      message: "Are you sure you want to delete this Lesson?",
    });

    if (modalState === ModalState.Canceled) return;
    try {
      loading.value = true;
      await _repo.deleteLesson(courseId, chapterId, lessonId);
      toast.success("Lesson deleted successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const uploadLessonAttachment = async (
    courseId: string,
    lessonId: string,
    request: CreateCourseContentRequestDto
  ) => {
    const { modalState, adminPassword } = await showConfirmationModel({
      title: "Upload Content",
      message: "Are you sure you want to upload this content?",
    });

    if (modalState === ModalState.Canceled) return;
    const formData = new FormData();
    formData.append("attachment", request.attachment);
    formData.append("resolution", request.resolution.toString());
    formData.append("numberOfSeconds", request.numberOfSeconds.toString());
    if (request.contentUrl) formData.append("contentUrl", request.contentUrl);

    try {
      const { data } = await _repo.createContent(
        courseId,
        lessonId,
        formData,
        (progressEvent: AxiosProgressEvent) => {
          uploadAtachmentProgress.value =
            Math.round((progressEvent.loaded * 100) / progressEvent!.total!) ||
            0;
        }
      );
      if (data?.message) toast.success(data?.message);
      uploadAtachmentProgress.value = 0;
      return data;
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    }
  };

  const deleteContent = async (courseId: string, lessonId: string) => {
    const { modalState, adminPassword } = await showConfirmationModel({
      title: "Delete Content",
      message: "Are you sure you want to delete this content?",
    });

    if (modalState === ModalState.Canceled) return;
    try {
      loading.value = true;
      await _repo.deleteContent(courseId, lessonId);
      toast.success("Content deleted successfully");
    } catch (e: any) {
      toast.error(e.response?.data?.messag || e.message);
      throw e;
    } finally {
      loading.value = false;
    }
  };

  return {
    state,
    loading,
    fetchCourse,
    fetchCourses,
    createCourse,
    updateCourse,
    deleteCourse,
    toggleCourseActivation,
    fetchChapters,
    fetchChapter,
    createChapter,
    updateChapter,
    deleteChapter,
    fetchLessons,
    fetchLesson,
    updateLesson,
    createLesson,
    deleteLesson,
    uploadLessonAttachment,
    deleteContent,
    uploadAtachmentProgress,
  };
});
